import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { SmartLink } from '../SmartLink';
import type { ProductFaq as Faq } from '../../types/product';
type ProductFaqProps = {
  heading: string;
  items: Faq[];
};
const EASE = [0.23, 1, 0.32, 1] as const;
export function ProductFaq({
  heading,
  items
}: ProductFaqProps) {
  const [open, setOpen] = useState<number | null>(0);
  const reduceMotion = useReducedMotion();
  return <section className="w-full bg-canvas-sky py-20" aria-labelledby="faq-heading" data-id="element-1174" data-mp="16k2q24cy1d2h">
      <div className="mx-auto grid max-w-shell grid-cols-1 gap-10 px-6 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-16" data-id="element-1175" data-mp="1bhl0jz6m00cl">
        <h2 id="faq-heading" className="display text-[30px] font-semibold text-ink sm:text-[40px]" data-id="element-1176" data-mp="1gbfjh0ee4bam">
          {heading}
        </h2>

        <ul className="border-b border-canvas-line" data-id="element-1177" data-mp="nss481m2rqhg">
          {items.map((item, index) => {
          const isOpen = open === index;
          const panelId = `faq-panel-${index}`;
          const buttonId = `faq-button-${index}`;
          return <li key={item.question} className="border-t border-canvas-line" data-id="element-1178" data-mp="1lwjuwx7qrug5" data-mpi={index}>
                <h3 data-id="element-1179" data-mp="1h3qqkag6qo4l">
                  <button id={buttonId} type="button" aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpen(isOpen ? null : index)} className="flex w-full items-center justify-between gap-6 py-5 text-left" data-id="element-1180" data-mp="1h2yxb6a292hh">
                    <span className="text-[17px] font-semibold tracking-[-0.01em] text-ink" data-id="element-1181" data-mp="y78s7a6tzpzd">
                      {item.question}
                    </span>
                    <span className={isOpen ? 'text-brand' : 'text-ink-muted'} aria-hidden="true" data-id="element-1182" data-mp="2sk8cvhq2jadi">
                      {isOpen ? <MinusIcon className="h-5 w-5" data-id="element-1183" data-mp="2oiwo1ef0qt6d" /> : <PlusIcon className="h-5 w-5" data-id="element-1184" data-mp="2ojyasg5ed43x" />}
                    </span>
                  </button>
                </h3>
                <AnimatePresence initial={false} data-id="element-1185" data-mp="ltvb2vh8jf86">
                  {isOpen && <motion.div id={panelId} role="region" aria-labelledby={buttonId} initial={reduceMotion ? {
                opacity: 0
              } : {
                height: 0,
                opacity: 0
              }} animate={reduceMotion ? {
                opacity: 1
              } : {
                height: 'auto',
                opacity: 1
              }} exit={reduceMotion ? {
                opacity: 0
              } : {
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.22,
                ease: EASE
              }} className="overflow-hidden" data-id="element-1186" data-mp="qo98t4jddyg0">
                      <div className="max-w-[620px] pb-6" data-id="element-1187" data-mp="10kckb10439fm">
                        <p className="text-[15px] leading-[1.65] text-ink-muted" data-id="element-1188" data-mp="10mp31gdp1ntq">{item.answer}</p>
                        {item.link && <SmartLink href={item.link.href} className="mt-3 inline-flex items-center gap-1.5 text-[15px] font-semibold text-brand transition-colors duration-150 ease-out hover:text-brand-hover" data-id="element-1189" data-mp="3lbd59khqgf2l">
                            {item.link.label}
                            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1190" data-mp="3mrh5ql19j6ys" />
                          </SmartLink>}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </li>;
        })}
        </ul>
      </div>
    </section>;
}